import React, { useState, useEffect, useRef } from 'react';
import Image from '@tiptap/extension-image';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';

const ResizableImageComponent = ({ node, updateAttributes, selected, editor }) => {
  const [width, setWidth] = useState(node.attrs.width || null);
  const [height, setHeight] = useState(node.attrs.height || null);
  const [isResizing, setIsResizing] = useState(false);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const currentSizeRef = useRef({ width: null, height: null });

  useEffect(() => {
    if (node.attrs.width) setWidth(node.attrs.width);
    if (node.attrs.height) setHeight(node.attrs.height);
  }, [node.attrs.width, node.attrs.height]);

  useEffect(() => {
    currentSizeRef.current = { width, height };
  }, [width, height]);

  const handleMouseDown = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const img = imageRef.current;
    if (!img) return;

    const startWidth = width || img.offsetWidth || img.naturalWidth || 300;
    const startHeight = height || img.offsetHeight || img.naturalHeight || 200;
    const aspectRatio = startWidth / startHeight;
    const minSize = 50;

    const handleMouseMove = (e) => {
      if (!img) return;

      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      let newWidth, newHeight;

      // Corner handles - maintain aspect ratio
      if (handle === 'top-left') {
        newWidth = Math.max(minSize, startWidth - deltaX);
        newHeight = newWidth / aspectRatio;
      } else if (handle === 'top-right') {
        newWidth = Math.max(minSize, startWidth + deltaX);
        newHeight = newWidth / aspectRatio;
      } else if (handle === 'bottom-left') {
        newWidth = Math.max(minSize, startWidth - deltaX);
        newHeight = newWidth / aspectRatio;
      } else if (handle === 'bottom-right') {
        newWidth = Math.max(minSize, startWidth + deltaX);
        newHeight = newWidth / aspectRatio;
      }
      // Edge handles - allow independent resizing
      else if (handle === 'top') {
        newHeight = Math.max(minSize, startHeight - deltaY);
        newWidth = newHeight * aspectRatio;
      } else if (handle === 'bottom') {
        newHeight = Math.max(minSize, startHeight + deltaY);
        newWidth = newHeight * aspectRatio;
      } else if (handle === 'left') {
        newWidth = Math.max(minSize, startWidth - deltaX);
        newHeight = newWidth / aspectRatio;
      } else if (handle === 'right') {
        newWidth = Math.max(minSize, startWidth + deltaX);
        newHeight = newWidth / aspectRatio;
      }

      setWidth(Math.round(newWidth));
      setHeight(Math.round(newHeight));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      const finalWidth = currentSizeRef.current.width;
      const finalHeight = currentSizeRef.current.height;
      if (finalWidth && finalHeight) {
        updateAttributes({
          width: finalWidth,
          height: finalHeight,
        });
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Get alignment from node attributes first, then check parent paragraph/heading
  const [alignment, setAlignment] = useState(node.attrs.align || 'left');
  
  useEffect(() => {
    // Update alignment when node attributes change
    setAlignment(node.attrs.align || 'left');
  }, [node.attrs.align]);
  
  useEffect(() => {
    if (editor && !node.attrs.align) {
      try {
        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;
        
        // Find parent paragraph or heading to get alignment
        for (let depth = $from.depth; depth > 0; depth--) {
          const parent = $from.node(depth);
          if (parent.type.name === 'paragraph' || parent.type.name === 'heading') {
            const align = parent.attrs.textAlign || 'left';
            if (align !== alignment) {
              setAlignment(align);
              // Update node attributes to persist alignment
              updateAttributes({ align });
            }
            return;
          }
        }
        
        // Default to left if no parent found
        if (alignment !== 'left') {
          setAlignment('left');
          updateAttributes({ align: 'left' });
        }
      } catch (e) {
        // Keep current alignment on error
      }
    }
  }, [editor, node, alignment, updateAttributes]);
  
  const getAlignmentStyle = () => {
    switch (alignment) {
      case 'center':
        return { 
          margin: '0 auto',
        };
      case 'right':
        return { 
          marginLeft: 'auto',
          marginRight: '0',
        };
      case 'left':
      default:
        return { 
          marginLeft: '0',
          marginRight: 'auto',
        };
    }
  };

  return (
    <NodeViewWrapper
      ref={containerRef}
      className={`resizable-image-wrapper ${selected ? 'selected' : ''}`}
      style={{ 
        position: 'relative',
        display: 'block',
        maxWidth: '100%',
        width: 'fit-content',
        ...getAlignmentStyle(),
      }}
    >
      <img
        ref={imageRef}
        src={node.attrs.src}
        alt={node.attrs.alt || ''}
        style={{
          width: width ? `${width}px` : 'auto',
          height: height ? `${height}px` : 'auto',
          maxWidth: '100%',
          display: 'block',
          cursor: isResizing ? 'grabbing' : 'default',
          userSelect: 'none',
          verticalAlign: 'top',
        }}
        draggable={false}
      />
      {selected && (
        <>
          {/* Corner handles */}
          <div
            className="resize-handle resize-handle-top-left"
            onMouseDown={(e) => handleMouseDown(e, 'top-left')}
          />
          <div
            className="resize-handle resize-handle-top-right"
            onMouseDown={(e) => handleMouseDown(e, 'top-right')}
          />
          <div
            className="resize-handle resize-handle-bottom-left"
            onMouseDown={(e) => handleMouseDown(e, 'bottom-left')}
          />
          <div
            className="resize-handle resize-handle-bottom-right"
            onMouseDown={(e) => handleMouseDown(e, 'bottom-right')}
          />
          {/* Edge handles */}
          <div
            className="resize-handle resize-handle-top"
            onMouseDown={(e) => handleMouseDown(e, 'top')}
          />
          <div
            className="resize-handle resize-handle-right"
            onMouseDown={(e) => handleMouseDown(e, 'right')}
          />
          <div
            className="resize-handle resize-handle-bottom"
            onMouseDown={(e) => handleMouseDown(e, 'bottom')}
          />
          <div
            className="resize-handle resize-handle-left"
            onMouseDown={(e) => handleMouseDown(e, 'left')}
          />
        </>
      )}
    </NodeViewWrapper>
  );
};

export const ResizableImage = Image.extend({
  name: 'image',
  group: 'block',
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element) => {
          const width = element.getAttribute('width');
          return width ? parseInt(width, 10) : null;
        },
        renderHTML: (attributes) => {
          if (!attributes.width) {
            return {};
          }
          return {
            width: attributes.width,
          };
        },
      },
      height: {
        default: null,
        parseHTML: (element) => {
          const height = element.getAttribute('height');
          return height ? parseInt(height, 10) : null;
        },
        renderHTML: (attributes) => {
          if (!attributes.height) {
            return {};
          }
          return {
            height: attributes.height,
          };
        },
      },
      align: {
        default: 'left',
        parseHTML: (element) => {
          const align = element.getAttribute('data-align') || element.style.textAlign || 'left';
          return align;
        },
        renderHTML: (attributes) => {
          if (!attributes.align || attributes.align === 'left') {
            return {};
          }
          return {
            'data-align': attributes.align,
            style: `text-align: ${attributes.align};`,
          };
        },
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageComponent);
  },
});
